import { ISession, createRuntime } from '@augloop/runtime-client';
import { getAugloopServiceUrl } from './getAugloopServiceUrl';
import { getGuid } from 'owa-guid';
import { logALTelemetryEvent } from './logALTelemetryEvent';
import { requestAuthToken } from './requestAuthToken';
import { IOperation, AddOperation, Document } from '@augloop/types-core';
import { trace } from 'owa-trace';
import { logUsage } from 'owa-analytics';
import { getFlightsToTrack } from 'owa-editor-augloop-plugin-utils/lib/getFlightsToTrack';

const SESSION_CLOSE_ERROR = 'AugLoopSessionCloseError';

let augloopSession: { [augloopSessionId: string]: Promise<ISession | null> } = {};

/**
 * The he value is a Promise, is allow each plugin that need
 * to use the same augloop session to await for it
 */
export function getAugLoopSession(augloopSessionId: string): Promise<ISession | null> {
    if (augloopSession[augloopSessionId]) {
        trace.info('[augloopSession] cached augloop session. Id:' + augloopSessionId);
    } else {
        trace.info('[augloopSession] Initialize augloop session. Id:' + augloopSessionId);
        augloopSession[augloopSessionId] = initializeAugmentationLoop(augloopSessionId);
    }
    return augloopSession[augloopSessionId];
}

async function initializeAugmentationLoop(augloopSessionId: string): Promise<ISession | null> {
    let clientRuntime = createRuntime();

    if (clientRuntime) {
        await clientRuntime.init(
            getAugloopServiceUrl(),
            {
                appName: 'Outlook',
                appPlatform: 'Web',
                appVersion: '1.0',
                releaseAudienceGroup: undefined,
                releaseChannel: undefined,
                releaseFork: undefined,
                sessionId: augloopSessionId,
                flights: getFlightsToTrack().join(';'),
            },
            {
                onResult: onResult,
                onAnnotationResult: onAnnotationResult,
                sendTelemetryEvent: logALTelemetryEvent,
                requestAuthToken: requestAuthToken,
                isFeatureEnabled: isFeatureEnabled,
                executeLocalLambda: executeLocalLambda,
                areLicenseFeaturesEnabled: areLicenseFeaturesEnabled,
            },
            {
                telemetryAggregationIntervalSec: 30,
                initSession: false,
            }
        );

        return clientRuntime
            .createSession({
                docSessionId: getGuid(),
            })
            .then(session => {
                submitParentTileOperation(session);
                return session;
            });
    }
    return Promise.resolve(null);
}

/**
 * Submits the document tile as a seed operation to AugLoop so that any content
 * that gets submitted on this session does not get held up on the service.
 */
function submitParentTileOperation(augLoopSession: ISession | null): void {
    if (!augLoopSession) {
        trace.info('[augloopSession]. Unable to submit seed operation, session is undefined');
        return;
    }
    const addNewDocumentOperation = new AddOperation();
    addNewDocumentOperation.parentPath = ['session'];
    const rootDocument = new Document();
    addNewDocumentOperation.items = [
        {
            id: 'doc',
            body: rootDocument,
        },
    ];

    augLoopSession.submitSeedOperations([addNewDocumentOperation]);
}

function onResult(inputSchema: string, input: any, outputSchema: string, output: any): void {
    // TODO: NYI
}

async function isFeatureEnabled(featureName: string, scope: string): Promise<boolean> {
    // For now return true for all features
    trace.info('[augloopSession] isFeatureEnabled callback: feature name: ' + featureName);
    return true;
}

function executeLocalLambda(
    inputSchema: string,
    input: any,
    outputSchema: string,
    config?: any
): Promise<any> {
    // TODO: NYI
    if (inputSchema && input && outputSchema && config) {
        return Promise.resolve(null);
    }
    return Promise.resolve(null);
}

function areLicenseFeaturesEnabled(_licenseFeatures: number[]): Promise<boolean> {
    // TODO: NYI
    return Promise.resolve(false);
}

function onAnnotationResult(operation: IOperation): void {}

export async function disposeAugLoopSession(augloopSessionId: string) {
    const currentAugloopSession = await augloopSession[augloopSessionId];
    if (currentAugloopSession) {
        try {
            currentAugloopSession.close();
        } catch (error) {
            logUsage(SESSION_CLOSE_ERROR, { error: error.message }, { logEvery: 1 });
            trace.info(
                `[augloopSession] exception encountered when closing augLoop session: ${error.message}`
            );
        }
        trace.info('[augloopSession] disposed augloop session. Id:' + augloopSessionId);
        delete augloopSession[augloopSessionId];
    }
}
